import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DollarSign, Users, TrendingUp, ArrowRight, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthDialog from "./AuthDialog";

const benefits = [
  {
    icon: DollarSign,
    title: "Competitive Commission",
    description: "Earn 10% on every first purchase from customers you refer",
  },
  {
    icon: Users,
    title: "30-Day Cookie",
    description: "Get credit for referrals up to 30 days after initial click",
  },
  {
    icon: TrendingUp,
    title: "Real-Time Tracking",
    description: "Monitor your referrals and earnings with our dashboard",
  },
  {
    icon: Wallet,
    title: "Easy Payouts",
    description: "Get paid via Stripe Connect with just $25 minimum",
  },
];

export function AffiliateSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  const handleBecomeAffiliate = () => {
    if (user) {
      // User is logged in, navigate directly
      navigate("/affiliate/apply");
    } else {
      // User is not logged in, show auth dialog
      setIsAuthDialogOpen(true);
    }
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600 text-sm font-medium mb-4">
            <DollarSign className="w-4 h-4" />
            <span>Affiliate Program</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Earn Money Sharing <span className="text-primary">What You Love</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join our affiliate program and earn commissions by sharing StickersBy.AI with your
            audience. Perfect for creators, influencers, and anyone who loves custom products.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-background rounded-xl p-6 shadow-sm border border-border/50 hover:shadow-md hover:border-primary/20 transition-all"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
              <p className="text-sm text-muted-foreground">{benefit.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Button size="lg" className="group" onClick={handleBecomeAffiliate}>
            Become an Affiliate
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            Free to join • No approval wait • Start earning today
          </p>
        </div>
      </div>

      {/* Auth Dialog for unauthenticated users */}
      <AuthDialog
        isOpen={isAuthDialogOpen}
        onOpenChange={setIsAuthDialogOpen}
        redirectTo="/affiliate/apply"
      />
    </section>
  );
}
